import React, { createContext, useContext, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Heading,
  Text,
} from '@razorpay/blade/components';
import loanRequestSuccess from 'assets/loan/loan-request-success.png';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { WizardHeader } from 'components/WizardViews/components';
import { FormProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import {
  INITIAL_LOAN_REQUEST_FORM_DATA,
  CreateLoanRequestFormData,
  CreateLoanRequestProps,
} from './types';
import RequestDetails from './RequestDetails';
import AmountDetails from './AmountDetails';
import Review from './Review';

export const LoanSuccessContext = createContext<{
  setLoanId: (id: number) => void;
}>({ setLoanId: () => {} });

export const useLoanSuccess = () => useContext(LoanSuccessContext);

const SuccessModal = ({
  isOpen,
  loanId,
  onClose,
}: {
  isOpen: boolean;
  loanId: number | null;
  onClose: () => void;
}) => (
  <Modal isOpen={isOpen} onDismiss={onClose} size="small">
    <ModalHeader />
    <ModalBody>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="spacing.7"
        textAlign="center"
        paddingY="spacing.4">
        <img src={loanRequestSuccess} alt="Request received" width={73} height={73} />

        <Box display="flex" flexDirection="column" gap="spacing.2">
          {loanId && (
            <Text size="small" variant="body" weight="medium" color="surface.text.gray.normal">
              Request ID #{loanId}
            </Text>
          )}
          <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
            Your request has been received
          </Heading>
          <Text size="small" variant="body" weight="regular" color="surface.text.gray.subtle">
            Your request is now under review.
          </Text>
        </Box>

        <Text size="small" variant="body" weight="regular" color="surface.text.gray.subtle">
          Check your request status in the{' '}
          <Link href="/employee-loans" size="small">
            Dashboard
          </Link>{' '}
          under Loans & Advances.
        </Text>
      </Box>
    </ModalBody>
    <ModalFooter>
      <Button variant="secondary" color="primary" size="medium" isFullWidth onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
);

export const CreateOrEditLoanRequest = ({ isOpen, onClose, editLoan }: CreateLoanRequestProps) => {
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successLoanId, setSuccessLoanId] = useState<number | null>(null);

  const isEditMode = !!editLoan;

  const initialState: CreateLoanRequestFormData = editLoan
    ? {
        ...INITIAL_LOAN_REQUEST_FORM_DATA,
        principal: String(editLoan.principal),
        tenure: editLoan.tenure,
        recoveryStartFrom: editLoan.recoveryStartFrom,
        description: editLoan.description ?? '',
        loanCategoryId: editLoan.loanCategoryId ?? null,
        documentFileId: editLoan.documentFileId ?? null,
        editLoanId: editLoan.id,
      }
    : INITIAL_LOAN_REQUEST_FORM_DATA;

  const handleSuccess = () => {
    onClose();
    if (!isEditMode) {
      setIsSuccessOpen(true);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onClose} size="full">
        <LoanSuccessContext.Provider value={{ setLoanId: setSuccessLoanId }}>
          <FormProvider<CreateLoanRequestFormData> initialState={initialState}>
            <WizardInfoProvider
              initialStep={0}
              totalSteps={3}
              onWizardClose={onClose}
              onSuccess={handleSuccess}>
              <Box display="flex" flexDirection="column" height="100%">
                <WizardHeader heading="Create Request" subHeading="" onCloseClick={onClose} />
                <Box flex={1} overflowY="auto" display="flex" flexDirection="column">
                  <Wizard>
                    <WizardStep step={0} component={RequestDetails} />
                    <WizardStep step={1} component={AmountDetails} />
                    <WizardStep step={2} component={Review} />
                  </Wizard>
                </Box>
              </Box>
            </WizardInfoProvider>
          </FormProvider>
        </LoanSuccessContext.Provider>
      </Modal>

      <SuccessModal
        isOpen={isSuccessOpen}
        loanId={successLoanId}
        onClose={() => {
          setIsSuccessOpen(false);
          setSuccessLoanId(null);
        }}
      />
    </>
  );
};

export default CreateOrEditLoanRequest;
